import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

// Components
import Header from "../Header";
import Footer from "../Footer";
import { LabelComponent } from "UnitPlans/components/LabelComponent";
import { FieldComponent } from "UnitPlans/components/IBcomponents";
import CommentBox from "AppComponents/CommentBox";
import StepHeader from "AppComponents/StepHeader";
import ScreenBaseComponent from "AppComponents/ScreenBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import CommunityBasicDetails from "./CommunityBasicDetails";
import Worksheet from "Worksheet";

// third party
import { compose } from "lib/apolloClient";
import { withRouter } from "react-router";
import { connect } from "react-redux";

// Utils / services / constants
import { getElementValue } from "modules/ElementValueUtils";
import { fontStyle, colors } from "Constants";
import { getSubFields } from "UnitPlans/modules/UnitPlanModule";
import { PAComponent } from "lib/PermissionAware";
import {
  MODE_ASSESSMENT_VIEW,
  ASSESSMENT_TYPE_WORKSHEET,
} from "Worksheet/modules/WorksheetConstants";
import { PARENT, TASK_TYPE_WORKSHEET } from "Constants/stringConstants";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { getIsGradingPeriodUnlocked } from "Login/routes/Administrator/commonComponents/GradingSetup/modules/Utils";

// DS
import { fontStyles } from "@toddle-edu/ds-theme";

// Scss
import classes from "./MiddlePane.scss";

const globalStyles = {
  labelComponentStyles: {
    plannerIconProps: {
      isV2: true,
      plannerIconCustomStyle: {
        width: "auto",
        height: "auto",
      },
      variant: "subtle",
    },
    leftContainerStyle: { gap: "8px" },
    labelIconContainerStyle: {
      margin: "0px",
      alignItems: "center",
      minHeight: "28px",
    },
    labelStyle: {
      padding: "0px",
      fontSize: "1.6rem",
      ...fontStyles.demiBold,
      lineHeight: "2rem",
    },
  },
  subTextStyle: {
    position: "relative",
    left: "-26px",
  },
  fieldComponentStyles: {
    labelContainerStyle: {
      marginBottom: "8px",
    },
  },
  contentStyle: {
    fontSize: "1.4rem",
    lineHeight: "2rem",
    ...fontStyle.medium,
    color: colors.textDefault,
  },
};

class MiddlePane extends ScreenBaseComponent {
  constructor(props) {
    super(props);
    this.container = null;
    this.state = {
      currentStep: "ASSESSMENT_DETAILS",
    };
    this.Refs = {};

    const { t } = this.props;
    this.worksheetSteps = [
      {
        label: t("common:details"),
        value: "ASSESSMENT_DETAILS",
        heading: t("common:details"),
        subHeading: t("classRoom:classes_discussion_shared_with"),
      },
      {
        label: t("worksheet:questions"),
        value: "QUESTION_CREATION",
        heading: t("worksheet:questions"),
        subHeading: t("classRoom:classes_discussion_shared_with"),
      },
    ];
  }

  getRightPaneComponent = () => {
    const {
      resourceId,
      parentType,
      resourceDetails,
      isCommunity,
      userLoggedIn,
      testId,
    } = this.props;

    const { currentRightPane } = this.state;

    const type = isCommunity ? "COMMUNITY_ASSESSMENT" : parentType;

    if (isCommunity && !userLoggedIn) return null;

    switch (currentRightPane) {
      case "comment":
        return (
          <CommentBox
            parentType={type}
            nodeId={resourceId}
            type={parentType}
            headerStyle={{ border: "none" }}
            feedStyle={{ paddingTop: 0, overflowY: "auto" }}
            commentBoxContainerStyle={{ minHeight: 70 }}
            onClose={this.onCommentClick}
            collaborators={_.get(resourceDetails, "unitPlan.collaborators", [])}
            isRightPaneChat={true}
            allowReply={isCommunity}
            disableTagging={isCommunity}
            canCreateAudioComment={!isCommunity}
            // canCreateComment={!isPublicAccess}
            testId={testId}
          />
        );
      default:
        return null;
    }
  };

  getDependentFieldValue = ({ dependentFieldId }) => {
    const { resourceFields } = this.props;

    const dependentFieldData = _.find(resourceFields, {
      uid: dependentFieldId,
    });

    if (_.isEmpty(dependentFieldData) || _.isEmpty(dependentFieldId)) {
      return null;
    }

    return _.get(dependentFieldData, "value");
  };

  getSteps = () => {
    const {
      t,
      view_steps: viewSteps = [],
      step_list: stepList,
      setStepsRef,
      isCommunity,
      isToddleLibrary,
    } = this.props;

    return _.map(viewSteps, (step, index) => {
      const { label, viewLabel, fields, subtext, viewSubtext } =
        stepList[step] || {};

      const stepLabel = viewLabel || label;
      const stepSubText = viewSubtext || subtext;

      const isLastStep = index === _.size(viewSteps) - 1;

      return (
        <>
          <div
            key={step}
            className={classes.stepContainer}
            ref={ref => setStepsRef({ ref, key: step })}
          >
            {["basicV2", "s1", "v1"].includes(step) &&
            (isCommunity || isToddleLibrary) ? (
              <CommunityBasicDetails {...this.props} />
            ) : (
              <>
                {!_.isEmpty(stepLabel) ? (
                  <div className={classes.stepLabel}>
                    <div className={classes.headerTxtContainer}>
                      <div className={classes.textContainer}>
                        <LabelComponent
                          t={t}
                          label={label}
                          showComment={false}
                          subText={stepSubText}
                          {...globalStyles.labelComponentStyles}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className={classes.fieldsContainer}>
                  {this.getFields({ fields })}
                </div>
              </>
            )}
          </div>
          {!isLastStep ? <div className={classes.separator} /> : null}
        </>
      );
    });
  };

  getFields = ({ fields }) => {
    const {
      field_list,
      resourceId,
      unitPlanFields,
      resourceFields,
      benchmarkSet,
      parentType,
      t,
      isLocalisedTemplate,
      isCommunity = false,
      isToddleLibrary = false,
      subscribeToddleLibraryEvents,
      unSubscribeToddleLibraryEvents,
      assessmentTools,
      resourceDetails,
      plannerElements,
      getCurrentAssessmentTypes,
      testId,
      isDownloadEnabled,
      curriculumProgram,
      curriculumType,
      learningCourseId,
      unitCourseId,
    } = this.props;

    const academicTermId = _.get(resourceDetails, "academicTerm.id", "");
    const isLearningGoalsEvaluationEnabled = _.get(
      resourceDetails,
      "isLearningGoalsEvaluationEnabled",
      false
    );
    const weightage = _.get(resourceDetails, "weightage", null);
    const category = _.get(resourceDetails, "category", {});

    const currentAssessmentTypes = getCurrentAssessmentTypes();

    return _.map(fields, fieldKey => {
      const field = field_list[fieldKey] ? field_list[fieldKey] : {};
      const {
        viewType,
        config: {
          viewLabel,
          subtext,
          styles,
          options,
          fieldTypeConfig = {},
          icon = null,
          dependentFieldId,
          plannerElementConfig = {},
        } = {},
      } = field;

      const label = _.get(field, "config.label");

      const assessmentFieldData = _.find(resourceFields, { uid: fieldKey });
      const assessmentFieldValue = _.get(assessmentFieldData, "value", "");

      const unitPlanFieldData = _.find(unitPlanFields, { uid: fieldKey });
      const unitPlanFieldValue = _.get(unitPlanFieldData, "value", "");

      const resolvedValue =
        _.get(assessmentFieldData, "resolvedMinimalTree", "") || {};

      const subjectIds = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "value",
        ""
      );

      const fieldId = _.get(assessmentFieldData, "id", "");
      const attachments = _.get(assessmentFieldData, "attachments", []);

      const value = getElementValue({
        fieldUID: fieldKey,
        valueKeys: assessmentFieldValue,
        unitPlanFieldValue,
        benchmarkSet,
        fieldObj: field,
        subjectIds: subjectIds,
        resolvedValue: resolvedValue,
        t,
        isLocalisedTemplate,
        isCommunity,
        isToddleLibrary,
        plannerElements,
        currentAssessmentTypes,
      });

      const fieldStyle = {
        ..._.get(styles, "style", {}),
        ..._.get(styles, "viewStyle", {}),
      };

      const fieldLabel = viewLabel || label;

      let subTextStyle;
      if (icon) {
        subTextStyle = globalStyles.subTextStyle;
      }

      const labelComponent = fieldLabel ? (
        <LabelComponent
          icon={icon}
          label={fieldLabel}
          subText={subtext}
          {...globalStyles.labelComponentStyles}
          subTextStyle={subTextStyle}
        />
      ) : null;

      return (
        <div key={fieldKey} className={classes.item} style={fieldStyle}>
          <div className={classes.contentWrapper}>
            <FieldComponent
              label={fieldLabel}
              fieldTypeConfig={fieldTypeConfig}
              applyDefaultClass={false}
              defaultOptions={options || []}
              subFields={getSubFields({
                fieldKey,
                fieldTemplateObj: field_list[fieldKey],
                fieldDataObj: assessmentFieldData,
                unitPlanFieldDataObj: unitPlanFieldData,
              })}
              attachments={attachments}
              customRef={(key, ref) => (this.Refs[key] = ref)}
              type={viewType}
              mode={"view"}
              parentType={parentType}
              parentId={resourceId}
              value={value}
              fieldId={fieldId}
              fieldKey={fieldKey}
              isAssessmentCreationRevamp={true}
              assessmentTools={assessmentTools}
              showToolPrintOption={true}
              unitPlanFields={unitPlanFields}
              assessmentFields={resourceFields}
              field_list={field_list}
              isApplyTextViewcustomTextStyle={true}
              labelComponent={labelComponent}
              sourceFrom={"UNIT_PLAN"}
              dependentFieldValue={this.getDependentFieldValue({
                dependentFieldId,
              })}
              isLearningGoalsEvaluationEnabled={
                isLearningGoalsEvaluationEnabled
              }
              academicTermId={academicTermId}
              category={category}
              weightage={weightage}
              styles={globalStyles.fieldComponentStyles}
              contentStyle={globalStyles.contentStyle}
              plannerElementConfig={plannerElementConfig}
              isToddleLibrary={isToddleLibrary}
              subscribeToddleLibraryEvents={subscribeToddleLibraryEvents}
              unSubscribeToddleLibraryEvents={unSubscribeToddleLibraryEvents}
              testId={`${testId}-${_.camelCase(label)}`}
              isDownloadEnabled={isDownloadEnabled}
              curriculumProgram={curriculumProgram}
              curriculumType={curriculumType}
              learningCourseId={learningCourseId}
              unitCourseId={unitCourseId}
            />
          </div>
        </div>
      );
    });
  };

  onCommentClick = () => {
    this.handleRightPane({
      rightPane: "comment",
      testId: "ResourceView-middlePane",
    });
  };

  renderSteps = () => {
    const { currentStep } = this.state;
    const {
      t,
      resourceDetails,
      isCommunity,
      tags,
      updateField,
      userLoggedIn,
      curriculumType,
      isDemo,
      showFooter,
      isToddleLibrary,
      testId,
      isDownloadEnabled,
    } = this.props;

    const { id: resourceId, createdBy, originalCreatedBy, communityComments } =
      resourceDetails || {};
    const isOriginalCreator =
      _.get(createdBy, "id") === _.get(originalCreatedBy, "id");

    const hideFooter =
      isOriginalCreator ||
      !originalCreatedBy ||
      !showFooter ||
      isDemo ||
      isToddleLibrary;

    switch (currentStep) {
      case "ASSESSMENT_DETAILS":
        return (
          <div
            className={classes.stepList}
            data-test-id={`${testId}-${resourceId}-container`}
          >
            {this.getSteps()}

            {!hideFooter ? (
              <Footer
                originalCreatedBy={originalCreatedBy}
                createdBy={_.get(resourceDetails, "createdBy", {})}
                communityComments={communityComments}
                curriculumType={curriculumType}
                isCommunity={isCommunity}
              />
            ) : null}

            {isCommunity && userLoggedIn && (
              <PAComponent perm={"CommunityPortal:AddOrUpdateEntityTags"}>
                <div className={classes.tags}>
                  <div className={classes.stepLabel}>{t("common:tags")}</div>
                  <FieldComponent
                    isDownloadEnabled={isDownloadEnabled}
                    newTagType={"TOPIC"}
                    type="SearchBoxWithTagQuery"
                    tagType={["TOPIC", "CATEGORY"]}
                    value={tags}
                    fieldKey="tags"
                    updateInputField={updateField}
                    hasAddRemoveValues={true}
                    allowAddNewOption={true}
                    placeholder={t("common:add_with_label", {
                      label: t("common:tags"),
                    })}
                  />
                </div>
              </PAComponent>
            )}
          </div>
        );
      case "QUESTION_CREATION":
        return (
          <Worksheet
            mode={MODE_ASSESSMENT_VIEW}
            parentId={resourceId}
            parentType={"ASSESSMENT"}
          />
        );
    }
  };

  onClickStep = value => {
    this.setState({ currentStep: value });
  };

  render() {
    const {
      userType,
      onScroll,
      totalCommentCount,
      unreadCommentCount,
      resourceFields,
      onClickPrint,
      parentType,
      resourceDetails,
      onClickEdit,
      isEditPermission,
      isCommunity,
      isToddleLibrary,
      image,
      onClose,
      userLoggedIn,
      isDemo,
      showBackButton,
      showViewActions,
      assessmentType,
      taskTypeGroup,
      isAssessmentLoading,
      subscribeToddleLibraryEvents,
      unSubscribeToddleLibraryEvents,
      testId,
      learningCourseId,
    } = this.props;

    const { id: resourceId, entityRatings, communityComments, likes, savedBy } =
      resourceDetails || {};

    const { currentRightPane, currentStep } = this.state;

    const stepsContainerClasses = classNames(classes.stepsContainer, {
      [classes.worksheetStepContainer]: currentStep == "QUESTION_CREATION",
    });

    // isLibraryPremium tells if the content is premium or not
    // showPremiumBanner will be true when the content is premium and user is not subscribed to it
    const showPremiumBanner = _.get(
      resourceDetails,
      "showLibraryPremiumBannerForUser",
      false
    );

    const isLibraryPremium = _.get(resourceDetails, "isLibraryPremium", false);

    const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
      _.get(resourceDetails, "academicTerm"),
      assessmentType,
      taskTypeGroup
    );

    return (
      <div
        className={classes.container}
        onScroll={e => onScroll(e)}
        ref={ref => (this.container = ref)}
      >
        <Header
          isDemo={isDemo}
          userType={userType}
          isCommunity={isCommunity}
          isToddleLibrary={isToddleLibrary}
          createdBy={_.get(resourceDetails, "createdBy", {})}
          showPremiumBanner={showPremiumBanner}
          isLibraryPremium={isLibraryPremium}
          fields={resourceFields}
          isCommentActive={currentRightPane == "comment"}
          onCommentClick={this.onCommentClick}
          totalCommentCount={totalCommentCount}
          unreadCommentCount={unreadCommentCount}
          parentType={parentType}
          parentId={resourceId}
          onClickPrint={onClickPrint}
          isEditPermission={isEditPermission}
          onClickEdit={onClickEdit}
          entityRatings={entityRatings}
          image={image}
          communityComments={communityComments}
          showViewActions={showViewActions}
          assessmentType={assessmentType || "le"}
          taskTypeGroup={taskTypeGroup}
          showBackButton={showBackButton}
          onClose={onClose}
          likes={likes}
          savedBy={savedBy}
          isGradingPeriodUnlocked={isGradingPeriodUnlocked}
          isAssessmentLoading={isAssessmentLoading}
          subscribeToddleLibraryEvents={subscribeToddleLibraryEvents}
          unSubscribeToddleLibraryEvents={unSubscribeToddleLibraryEvents}
          testId={testId}
          learningCourseId={learningCourseId}
        />
        {(assessmentType == ASSESSMENT_TYPE_WORKSHEET ||
          taskTypeGroup === TASK_TYPE_WORKSHEET) &&
        !_.isEqual(userType, PARENT) ? (
          <StepHeader
            steps={this.worksheetSteps}
            currentStep={currentStep}
            onClickStep={this.onClickStep}
            testId={testId}
          />
        ) : null}
        <div className={classes.innerContainer}>
          <div className={stepsContainerClasses}>{this.renderSteps()} </div>
          {currentRightPane ? this.renderRightPane() : null}
        </div>
      </div>
    );
  }
}

const mapActionCreators = {
  getCurrentAssessmentTypes,
};

MiddlePane.propTypes = {
  isLocalisedTemplate: PropTypes.bool,
  rightPaneWidth: PropTypes.number,
  testId: PropTypes.string,
};

MiddlePane.defaultProps = {
  isLocalisedTemplate: true,
  showBackButton: true,
  showFooter: true,
  rightPaneWidth: 440,
  testId: "resourceView-middlePane",
};

export default compose(
  I18nHOC({ resource: ["unitPlanTemplate", "common"] }),
  connect(null, mapActionCreators),
  withRouter
)(MiddlePane);
